import { VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS } from '../domain/voiceEventConstants';
import {
  classifyVoiceAnswerError,
  isVoiceAnswerRequestTimeout,
  isVoiceProviderAnswerFailed,
  VOICE_ANSWER_ERROR
} from '../domain/voiceAnswerErrors';
import { isVoiceAnswerConflictAcceptable } from '../infrastructure/api/VoiceCallsApiClient';
import {
  voiceRemoveIncomingCall,
  voiceReleaseCallSession,
  voiceSetCurrentCallControlId,
  voiceSetError,
  voiceSetLoading
} from '../../../../actions/voice/voiceActions';
import {
  answerCurrentAgentCall,
  markAgentCallAnswerPending,
  clearAgentAnswerBridgeOwnership,
  getCurrentAgentCall,
  isAgentCallDead,
  isAgentClientConnected,
  isAgentSessionAlreadyAnswered,
  scheduleAgentRemoteAudioPlayback,
  unlockAgentRemoteAudio,
  waitForIncomingAgentCall,
  waitForAgentClientReady,
  prefetchAgentLocalAudioStream,
  discardDeadAgentCall,
  abortPendingIncomingAgentCallWait,
  hangupCurrentAgentCall,
  AGENT_ANSWER_ABORTED
} from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import getOrCreateVoiceTabSessionId from '../infrastructure/webrtc/voiceTabSession';
import {
  claimVoiceTabAnswer,
  getVoiceTabClaimedCallControlId,
  markVoiceCallAnswerAttempted,
  markVoiceAnswerBridgeInFlight,
  clearVoiceAnswerBridgeInFlight,
  consumePendingMediaFailedHangup,
  releaseVoiceTabAnswer,
  completeVoiceCallHangup
} from './voiceHangupCoordinator';
import { VOICE_ENDED_REASON_HANGUP } from '../domain/voiceHangupEndedReason';
import { notifyVoiceIncomingCallAnswered } from './voiceIncomingCallAnsweredNotifier';

function isAgentIceConnected() {
  const state = getCurrentAgentCall()?.connection?.iceConnectionState;
  return state === 'connected' || state === 'completed';
}

/**
 * @param {unknown} httpError
 * @param {{ callId: string, answerAbandoned: boolean, dispatch: Function }} ctx
 * @returns {'resolved'|'wait_socket'|'throw'}
 */
function resolveAnswerHttpError(httpError, ctx) {
  if (isVoiceAnswerConflictAcceptable(httpError)) {
    ctx.onStopRingtone?.();
    if (httpError.voiceErrorCode === 'call_ended' || ctx.answerAbandoned) {
      releaseVoiceTabAnswer(ctx.callId);
      ctx.dispatch(voiceReleaseCallSession(ctx.callId));
      return 'resolved';
    }
    if (httpError.voiceErrorCode === 'call_already_answered') {
      releaseVoiceTabAnswer(ctx.callId);
      ctx.dispatch(voiceRemoveIncomingCall(ctx.callId));
      return 'resolved';
    }
  }

  if (isVoiceProviderAnswerFailed(httpError)) {
    if (isAgentIceConnected()) {
      // eslint-disable-next-line no-console
      console.warn(
        'Voice answer HTTP failed but agent ICE is connected; waiting for call_answered socket',
        httpError
      );
      return 'wait_socket';
    }
    ctx.onStopRingtone?.();
    releaseVoiceTabAnswer(ctx.callId);
    ctx.dispatch(voiceReleaseCallSession(ctx.callId));
    return 'fail';
  }

  if (isVoiceAnswerRequestTimeout(httpError)) {
    // eslint-disable-next-line no-console
    console.warn(
      'Voice answer HTTP still in progress after client timeout; waiting for call_answered socket',
      httpError
    );
    return 'wait_socket';
  }

  return 'throw';
}

/**
 * @param {{
 *   callId: string,
 *   hangupState: { activeCallControlId?: string|null, outboundDialingCallControlId?: string|null },
 *   dispatch: Function,
 *   answerCallApi: (callId: string) => Promise<unknown>,
 *   answerAbandonedRef?: { current: boolean },
 *   answerInFlightRef?: { current: boolean },
 *   disableButtons?: () => void,
 *   onStopRingtone?: () => void,
 *   onNotifyAnswered?: () => void
 * }} params
 * @returns {Promise<{ ok: true }|{ ok: false, ignored?: true, code?: string, error?: unknown }>}
 */
export default async function executeAnswerCallUseCase(params) {
  const {
    callId,
    hangupState,
    dispatch,
    answerCallApi,
    answerAbandonedRef = { current: false },
    answerInFlightRef = { current: false },
    disableButtons = () => {},
    onStopRingtone = () => window.stopRingtone?.(),
    onNotifyAnswered = notifyVoiceIncomingCallAnswered
  } = params;

  const activeId = hangupState.activeCallControlId;
  if (activeId && activeId !== callId) {
    // eslint-disable-next-line no-console
    console.warn('[voice-webrtc] answerCall ignored (tab already in a call)', {
      callId,
      activeId
    });
    return { ok: false, ignored: true };
  }

  if (!claimVoiceTabAnswer(callId)) {
    // eslint-disable-next-line no-console
    console.warn('[voice-webrtc] answerCall ignored (POST /answer already pending)', {
      callId,
      claimed: getVoiceTabClaimedCallControlId()
    });
    return { ok: false, ignored: true };
  }

  discardDeadAgentCall();

  if (answerInFlightRef.current) {
    const existing = getCurrentAgentCall();
    if (existing && !isAgentCallDead(existing)) {
      // eslint-disable-next-line no-console
      console.warn('[voice-webrtc] answerCall ignored (already in progress)', callId);
      releaseVoiceTabAnswer(callId);
      return { ok: false, ignored: true };
    }
    answerInFlightRef.current = false;
  }

  const existingSession = getCurrentAgentCall();
  if (
    existingSession?.direction === 'incoming'
    && isAgentSessionAlreadyAnswered(existingSession)
  ) {
    // eslint-disable-next-line no-console
    console.info('[voice-webrtc] JsSIP already answered; sending POST /answer anyway', callId);
  }

  answerInFlightRef.current = true;
  answerAbandonedRef.current = false;
  disableButtons();
  unlockAgentRemoteAudio();
  prefetchAgentLocalAudioStream().catch(() => {});
  markVoiceAnswerBridgeInFlight(callId);

  try {
    markVoiceCallAnswerAttempted(callId);
    markAgentCallAnswerPending(callId, { ownedByAnswerBridge: true });
    dispatch(voiceSetLoading(true));
    dispatch(voiceSetError(null));
    dispatch(voiceSetCurrentCallControlId(callId));
    await waitForAgentClientReady({ reason: 'answer', timeoutMs: 20_000 });
    if (!isAgentClientConnected()) {
      throw new Error('Timed out waiting for agent WebRTC leg');
    }
    const incomingCallPromise = waitForIncomingAgentCall({
      timeoutMs: VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS
    });
    const tabSessionId = getOrCreateVoiceTabSessionId();
    // eslint-disable-next-line no-console
    console.info('[voice-webrtc] POST /answer', { callId, tabSessionId });
    const answerHttpPromise = answerCallApi(callId);

    // Nest may reject (409 call_ended) before FreeSWITCH INVITEs this tab — stop waiting for SIP.
    answerHttpPromise.catch((httpError) => {
      const httpOutcome = resolveAnswerHttpError(httpError, {
        callId,
        answerAbandoned: answerAbandonedRef.current,
        dispatch,
        onStopRingtone
      });
      // eslint-disable-next-line no-console
      console.warn('[voice-webrtc] POST /answer failed while waiting for SIP INVITE', {
        callId,
        tabSessionId,
        outcome: httpOutcome,
        status: httpError?.status,
        voiceErrorCode: httpError?.voiceErrorCode,
        message: httpError instanceof Error ? httpError.message : String(httpError)
      });
      if (httpOutcome === 'fail' || httpOutcome === 'resolved') {
        answerAbandonedRef.current = true;
        abortPendingIncomingAgentCallWait(AGENT_ANSWER_ABORTED);
      }
    });

    // eslint-disable-next-line no-console
    console.info('[voice-webrtc] waiting for agent SIP INVITE / agent_sip_invite', {
      callId,
      tabSessionId
    });
    await answerCurrentAgentCall({ incomingCallPromise });
    scheduleAgentRemoteAudioPlayback();
    try {
      await answerHttpPromise;
      // eslint-disable-next-line no-console
      console.info('[voice-webrtc] POST /answer ok', { callId, tabSessionId });
    } catch (httpError) {
      const httpOutcome = resolveAnswerHttpError(httpError, {
        callId,
        answerAbandoned: answerAbandonedRef.current,
        dispatch,
        onStopRingtone
      });
      if (httpOutcome === 'resolved' || httpOutcome === 'wait_socket') {
        onNotifyAnswered();
        return { ok: true };
      }
      if (httpOutcome === 'fail') {
        return { ok: false, code: VOICE_ANSWER_ERROR.PROVIDER_ANSWER_FAILED, error: httpError };
      }
      throw httpError;
    }
    onNotifyAnswered();
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const aborted = answerAbandonedRef.current
      || error?.code === AGENT_ANSWER_ABORTED
      || message === AGENT_ANSWER_ABORTED;
    if (aborted) {
      onStopRingtone?.();
      return { ok: false, ignored: true, code: VOICE_ANSWER_ERROR.ABORTED };
    }
    markAgentCallAnswerPending(null);
    clearAgentAnswerBridgeOwnership();
    releaseVoiceTabAnswer(callId);
    const code = classifyVoiceAnswerError(error);
    // eslint-disable-next-line no-console
    console.warn('Voice WebRTC answer:', error);
    return { ok: false, code, error };
  } finally {
    clearVoiceAnswerBridgeInFlight(callId);
    answerInFlightRef.current = false;
    clearAgentAnswerBridgeOwnership();
    dispatch(voiceSetLoading(false));

    // mediaFailed while Contestar was open — hang up only after Nest answer finished.
    if (consumePendingMediaFailedHangup(callId)) {
      hangupCurrentAgentCall().catch(() => {});
      completeVoiceCallHangup({
        callControlId: callId,
        endedReason: VOICE_ENDED_REASON_HANGUP,
        onAfterHttpOk: () => {
          dispatch(voiceReleaseCallSession(callId));
        }
      }).catch(() => {});
    }
  }
}
